import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDashboardData } from '../api/api';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { FaChartBar, FaFileAlt, FaSpinner, FaChevronLeft, FaArrowUp, FaArrowDown, FaDollarSign } from 'react-icons/fa';

// Registrar componentes do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const RelatorioMensal = () => {
    const { mes } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [costChartData, setCostChartData] = useState({ labels: [], datasets: [] });
    const [insights, setInsights] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getDashboardData(mes);
                const data = res.data;

                if (data.status === 'Erro') throw new Error(data.mensagem);

                setData(data);
                prepareCharts(data);
            } catch (err) {
                setError(`Erro ao carregar dados do mês ${mes}: ${err.message || err.toString()}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [mes]);

    const prepareCharts = (data) => {
        const analitico = data.relatorio_analitico || {};
        const centros = Object.keys(analitico);
        const lucros = centros.map(c => analitico[c].Lucro);
        const custos = centros.map(c => analitico[c]['Custo Total']);

        // Gráfico de Lucro por Centro
        setChartData({
            labels: centros,
            datasets: [
                {
                    label: 'Resultado (R$)',
                    data: lucros,
                    backgroundColor: lucros.map(l => l >= 0 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)'),
                    borderRadius: 4,
                },
            ],
        });

        // Gráfico de Distribuição de Custos (Top 5)
        const sortedCustos = centros.map((c, i) => ({ label: c, value: custos[i] }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);

        setCostChartData({
            labels: sortedCustos.map(i => i.label),
            datasets: [
                {
                    data: sortedCustos.map(i => i.value),
                    backgroundColor: [
                        'rgba(59, 130, 246, 0.8)',
                        'rgba(168, 85, 247, 0.8)',
                        'rgba(236, 72, 153, 0.8)',
                        'rgba(249, 115, 22, 0.8)',
                        'rgba(107, 114, 128, 0.8)'
                    ],
                    borderWidth: 0,
                }
            ]
        });
    };

    const handleGenerateInsights = () => {
        if (!data || !data.relatorio_analitico) return;
        setIsGenerating(true);

        // Simulação de insight (em produção conectaria com LLM)
        setTimeout(() => {
            const analitico = data.relatorio_analitico;
            const sorted = Object.entries(analitico).sort(([, a], [, b]) => b.Lucro - a.Lucro);
            const melhor = sorted[0];
            const pior = sorted[sorted.length - 1];

            setInsights(`
                **Análise Executiva - ${mes}**
                
                1. **Destaque Positivo**: O centro **${melhor[0]}** apresentou o melhor desempenho, com lucro de **${formatCurrency(melhor[1].Lucro)}**.
                2. **Ponto de Atenção**: O centro **${pior[0]}** requer revisão imediata, acumulando prejuízo de **${formatCurrency(pior[1].Lucro)}**.
                3. **Recomendação**: Avaliar a alocação de custos indiretos para **${pior[0]}**, que representam ${(pior[1]['Custo Indireto'] / pior[1]['Custo Total'] * 100).toFixed(1)}% do custo total deste setor.
            `);
            setIsGenerating(false);
        }, 1500);
    };

    const formatCurrency = (val) => val ? val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';

    if (loading) return <div className="flex justify-center items-center h-screen"><FaSpinner className="animate-spin text-4xl text-indigo-600" /></div>;
    if (error) return <div className="p-6 bg-red-50 text-red-700 rounded-xl border border-red-200 m-4">Erro: {error}</div>;

    const resumo = data?.resumo || { total_receita: 0, total_custos: 0, lucro_prejuizo: 0 };
    const margem = resumo.total_receita ? ((resumo.lucro_prejuizo / resumo.total_receita) * 100).toFixed(1) : 0;

    return (
        <div className="min-h-screen bg-gray-50 p-6 font-sans">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                <div className="flex items-center mb-4 md:mb-0">
                    <button onClick={() => navigate(-1)} className="mr-4 p-2 bg-white rounded-full shadow-sm hover:bg-gray-100 text-gray-600 transition">
                        <FaChevronLeft />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Dashboard Financeiro</h1>
                        <p className="text-sm text-gray-500">Competência: {mes}</p>
                    </div>
                </div>
                <button
                    onClick={handleGenerateInsights}
                    disabled={isGenerating}
                    className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition flex items-center font-medium disabled:opacity-70"
                >
                    {isGenerating ? <FaSpinner className="animate-spin mr-2" /> : <FaFileAlt className="mr-2" />}
                    Gerar Insights IA
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KPICard
                    title="Receita Total"
                    value={formatCurrency(resumo.total_receita)}
                    icon={<FaDollarSign className="text-green-600" />}
                    trend="up"
                    color="green"
                />
                <KPICard
                    title="Custo Total"
                    value={formatCurrency(resumo.total_custos)}
                    icon={<FaArrowDown className="text-red-600" />}
                    trend="down"
                    color="red"
                />
                <KPICard
                    title="Resultado Operacional"
                    value={formatCurrency(resumo.lucro_prejuizo)}
                    icon={<FaChartBar className="text-blue-600" />}
                    color="blue"
                    isPositive={resumo.lucro_prejuizo >= 0}
                />
                <KPICard
                    title="Margem Líquida"
                    value={`${margem}%`}
                    icon={<div className="text-xs font-bold text-purple-600">%</div>}
                    color="purple"
                />
            </div>

            {/* Insights Section */}
            {insights && (
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-indigo-500 mb-8 animate-fade-in">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <FaFileAlt className="text-indigo-500 mr-2" /> Insights Inteligentes
                    </h3>
                    <div className="prose text-gray-600 whitespace-pre-line leading-relaxed">
                        {insights}
                    </div>
                </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Performance por Centro de Custo</h3>
                    <div className="h-80">
                        <Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Top 5 Custos</h3>
                    <div className="h-64 flex justify-center">
                        <Doughnut data={costChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                    </div>
                </div>
            </div>

            {/* Detailed Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-700">Detalhamento Financeiro</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-medium uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">Centro de Custo</th>
                                <th className="px-6 py-4 text-right">Receita</th>
                                <th className="px-6 py-4 text-right">Custo Direto</th>
                                <th className="px-6 py-4 text-right">Custo Indireto</th>
                                <th className="px-6 py-4 text-right">Resultado</th>
                                <th className="px-6 py-4 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.relatorio_analitico && Object.entries(data.relatorio_analitico).map(([centro, val]) => (
                                <tr key={centro} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-800">{centro}</td>
                                    <td className="px-6 py-4 text-right text-gray-600">{formatCurrency(val.Receita)}</td>
                                    <td className="px-6 py-4 text-right text-gray-600">{formatCurrency(val['Custo Direto'])}</td>
                                    <td className="px-6 py-4 text-right text-gray-600">{formatCurrency(val['Custo Indireto'])}</td>
                                    <td className={`px-6 py-4 text-right font-bold ${val.Lucro >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {formatCurrency(val.Lucro)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${val.Lucro >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {val.Lucro >= 0 ? 'Lucrativo' : 'Prejuízo'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const KPICard = ({ title, value, icon, color, isPositive }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 border-${color}-500 flex items-center justify-between hover:shadow-md transition`}>
        <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <h2 className={`text-2xl font-bold ${isPositive === false ? 'text-red-600' : 'text-gray-800'}`}>{value}</h2>
        </div>
        <div className={`p-3 bg-${color}-50 rounded-full text-${color}-600 text-xl`}>
            {icon}
        </div>
    </div>
);

export default RelatorioMensal;